import {
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Subject, debounceTime, distinctUntilChanged, switchMap } from 'rxjs';
import { LayoutService } from '../../layouts/layout.service';
import { FilterBarComponent, FilterState } from '../../shared/components/filter-bar/filter-bar';
import { DataTableComponent, ColumnDef } from '../../shared/components/data-table/data-table';
import { SelectOption } from '../../shared/components/select/select';
import { IncidentListItem as Incident, STAGE_CONFIG } from '../../shared/models/incident.models';
import { IncidentService } from '../../core/services/incident.service';
import { ProgramService } from '../../core/services/program.service';
import { CategoryService } from '../../core/services/category.service';

@Component({
  selector: 'app-incident-feed',
  imports: [FilterBarComponent, DataTableComponent],
  templateUrl: './incident-feed.html',
})
export class IncidentFeedComponent implements OnInit {
  private layout = inject(LayoutService);
  protected router = inject(Router);
  private incidentService = inject(IncidentService);
  private programService = inject(ProgramService);
  private categoryService = inject(CategoryService);
  private destroyRef = inject(DestroyRef);

  private categoriesTpl = viewChild.required<TemplateRef<{ $implicit: Incident }>>('categoriesTpl');
  private personsTpl = viewChild.required<TemplateRef<{ $implicit: Incident }>>('personsTpl');
  private stageTpl = viewChild.required<TemplateRef<{ $implicit: Incident }>>('stageTpl');

  protected loading = signal(false);
  protected incidents = signal<Incident[]>([]);
  protected total = signal(0);
  protected currentPage = signal(1);
  protected programOptions = signal<SelectOption[]>([]);
  protected categoryOptions = signal<SelectOption[]>([]);
  protected readonly stageOptions: SelectOption[] = [
    { value: '', label: 'All' },
    { value: 'A', label: 'Stage A' },
    { value: 'B', label: 'Stage B' },
    { value: 'C', label: 'Stage C' },
    { value: 'D', label: 'Stage D' },
    { value: 'Completed', label: 'Completed' },
  ];
  protected sortBy = signal('');
  protected sortDirection = signal<'asc' | 'desc'>('asc');
  private lastFilters: FilterState = {
    search: '',
    programId: '',
    categoryId: '',
    dateFrom: '',
    dateTo: '',
    stage: '',
  };

  private static readonly PAGE_SIZE = 10;

  protected columns = computed((): ColumnDef<Incident>[] => [
    {
      key: 'incidentDate',
      header: 'Date & Time',
      sortKey: 'incidentdate',
      cell: (i) => fmtIncidentDate(i.incidentDate),
      cellClass: 'font-bold text-navy-900 whitespace-nowrap',
    },
    {
      key: 'programName',
      header: 'Program',
      sortKey: 'programname',
      cell: (i) => i.programName,
      cellClass: 'font-semibold text-navy-800 whitespace-nowrap',
    },
    {
      key: 'locationName',
      header: 'Location',
      sortKey: 'locationname',
      cell: (i) => i.locationName ?? '',
      cellClass: 'text-navy-800 whitespace-nowrap',
    },
    {
      key: 'cats',
      header: 'Categories',
      sortKey: 'categories',
      cellTemplate: this.categoriesTpl(),
    },
    {
      key: 'persons',
      header: 'People Involved',
      sortKey: 'personsinvolved',
      cellTemplate: this.personsTpl(),
    },
    {
      key: 'stage',
      header: 'Report Stage',
      sortKey: 'currentstage',
      cellTemplate: this.stageTpl(),
    },
  ]);

  private filter$ = new Subject<
    FilterState & { sortBy: string; sortDirection: 'asc' | 'desc'; pageIndex: number }
  >();

  ngOnInit(): void {
    this.layout.set({
      title: 'Incidents',
      breadcrumb: '',
      showBackButton: false,
      switchPlaces: false,
      darkHeader: false,
      headerAction: {
        label: 'New Incident',
        icon: 'plus',
        onClick: () => this.router.navigate(['/incidents', 'new']),
      },
    });

    this.loadOptions();

    this.filter$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(
          (a, b) =>
            a.search === b.search &&
            a.programId === b.programId &&
            a.categoryId === b.categoryId &&
            a.dateFrom === b.dateFrom &&
            a.dateTo === b.dateTo &&
            a.stage === b.stage &&
            a.sortBy === b.sortBy &&
            a.sortDirection === b.sortDirection &&
            a.pageIndex === b.pageIndex,
        ),
        switchMap((f) => {
          this.loading.set(true);
          return this.incidentService.getIncidents({
            pageSize: IncidentFeedComponent.PAGE_SIZE,
            pageIndex: f.pageIndex,
            filter: f.search,
            programId: f.programId || undefined,
            categoryId: f.categoryId || undefined,
            dateFrom: f.dateFrom || undefined,
            dateTo: f.dateTo || undefined,
            stage: f.stage || undefined,
            sortBy: f.sortBy || undefined,
            sortDirection: f.sortDirection || undefined,
          });
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (res) => {
          this.incidents.set(res.incidents ?? []);
          this.total.set(res.total ?? 0);
          this.loading.set(false);
        },
        error: () => this.loading.set(false),
      });

    this.filter$.next({
      search: '',
      programId: '',
      categoryId: '',
      dateFrom: '',
      dateTo: '',
      stage: '',
      sortBy: '',
      sortDirection: 'asc',
      pageIndex: 0,
    });
  }

  private loadOptions(): void {
    this.programService
      .getProgramTypes()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          const programRecordingType = res.programTypes.find((elem) => elem.code === 'IR');
          this.programService
            .getPrograms({}, programRecordingType?.id)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: (res) =>
                this.programOptions.set([
                  { value: '', label: 'All' },
                  ...res.programs.map((p) => ({ value: p.id, label: p.name })),
                ]),
            });
        },
      });

    this.categoryService
      .getSituationCategories()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) =>
          this.categoryOptions.set([
            { value: '', label: 'All' },
            ...res.situationCategories.map((c) => ({ value: c.id, label: c.name })),
          ]),
      });
  }

  protected onFiltersChange(filters: FilterState): void {
    this.lastFilters = filters;
    this.currentPage.set(1);
    this.filter$.next({
      ...filters,
      sortBy: this.sortBy(),
      sortDirection: this.sortDirection(),
      pageIndex: 0,
    });
  }

  protected onSortChange(sort: { key: string; direction: 'asc' | 'desc' }): void {
    this.sortBy.set(sort.key);
    this.sortDirection.set(sort.direction);
    this.currentPage.set(1);
    this.filter$.next({
      ...this.lastFilters,
      sortBy: sort.key,
      sortDirection: sort.direction,
      pageIndex: 0,
    });
  }

  protected onPageChange(page: number): void {
    this.currentPage.set(page);
    this.filter$.next({
      ...this.lastFilters,
      sortBy: this.sortBy(),
      sortDirection: this.sortDirection(),
      pageIndex: page - 1,
    });
  }

  protected onRowAction(incident: Incident): void {
    this.router.navigate(['/incidents', incident.id]);
  }

  protected stageStyle(stage: string): { background: string; color: string } {
    const cfg = STAGE_CONFIG[stage];
    if (cfg) return { background: cfg.bg, color: cfg.color };
    return { background: '#f1f5f9', color: '#475569' };
  }
}

function fmtIncidentDate(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '—';
  const date = d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' });
  const time = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
  return `${date} ${time}`;
}
